import React, { useEffect, useState, useMemo, useCallback } from "react";
import DataTable from "react-data-table-component";
import Header from "../Header";
import Footer from "../Footer";
import API_BASE_URL from "../../Config";
import { Link } from "react-router-dom";

/* ────────── SVG Icon System ────────── */
const Icon = ({ d, size = 18, stroke = 1.8 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "block", flexShrink: 0 }}
  >
    {d}
  </svg>
);

const SearchIcon       = (p) => <Icon {...p} d={<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>} />;
const PlusIcon         = (p) => <Icon {...p} d={<><path d="M12 5v14M5 12h14" /></>} />;
const EditIcon         = (p) => <Icon {...p} d={<><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" /></>} />;
const TrashIcon        = (p) => <Icon {...p} d={<><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6 18 20a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /></>} />;
const CalendarIcon     = (p) => <Icon {...p} d={<><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>} />;
const ChevronLeftIcon  = (p) => <Icon {...p} d={<><path d="m15 18-6-6 6-6" /></>} />;
const ChevronRightIcon = (p) => <Icon {...p} d={<><path d="m9 18 6-6-6-6" /></>} />;
const CheckIcon        = (p) => <Icon {...p} d={<><path d="M20 6 9 17l-5-5" /></>} />;
const XCircleIcon      = (p) => <Icon {...p} d={<><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6M9 9l6 6" /></>} />;
const CloseIcon        = (p) => <Icon {...p} d={<><path d="M18 6 6 18M6 6l12 12" /></>} />;
const CheckCircleIcon  = (p) => <Icon {...p} d={<><circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" /></>} />;
const UserMdIcon       = (p) => <Icon {...p} d={<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>} />;
const AlertTriangleIcon= (p) => <Icon {...p} d={<><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><path d="M12 9v4" /><path d="M12 17h.01" /></>} />;

/* ────────── Multi-Select Dropdown ────────── */
const MultiSelect = ({ options, value, onChange, placeholder = "Filter…" }) => {
  const [open, setOpen] = useState(false);

  const toggleOption = (opt) => {
    onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]);
  };
  const clearAll = (e) => { e.stopPropagation(); onChange([]); };

  return (
    <div className="bc-multiselect" style={{ position: "relative" }}>
      <div
        className="bc-multiselect__trigger"
        onClick={() => setOpen(!open)}
        tabIndex={0}
        onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="bc-multiselect__label">
          {value.length === 0 ? placeholder : value.map((v) => (
            <span key={v} className="bc-multiselect__tag">{v}</span>
          ))}
        </span>
        <span className="bc-multiselect__caret">
          {value.length > 0 && (
            <button className="bc-multiselect__clear" onClick={clearAll} tabIndex={-1} type="button">
              <XCircleIcon size={14} />
            </button>
          )}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .2s ease" }}>
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </div>
      {open && (
        <div className="bc-multiselect__dropdown" role="listbox" aria-multiselectable="true">
          {options.map((opt) => (
            <div
              key={opt}
              className={`bc-multiselect__option ${value.includes(opt) ? "is-selected" : ""}`}
              role="option"
              aria-selected={value.includes(opt)}
              onClick={() => toggleOption(opt)}
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && toggleOption(opt)}
            >
              <span className="bc-multiselect__check">
                {value.includes(opt) && <CheckIcon size={12} stroke={2.5} />}
              </span>
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ────────── iPhone-style Toggle ────────── */
const ToggleSwitch = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={onChange}
    className={`dept-toggle ${checked ? "is-on" : "is-off"}`}
    title={checked ? "Click to deactivate" : "Click to activate"}
  >
    <span className="dept-toggle__thumb" />
  </button>
);

/* ────────── Custom Pagination ────────── */
const CustomPagination = ({ currentPage, totalPages, onPageChange, perPage, totalRows, onPerPageChange }) => {
  const pages = useMemo(() => {
    const arr = [];
    const delta = 2;
    const left = currentPage - delta;
    const right = currentPage + delta + 1;
    let l;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= left && i < right)) arr.push(i);
    }
    const withEllipsis = [];
    arr.forEach((i) => {
      if (l) {
        if (i - l === 2) withEllipsis.push(l + 1);
        else if (i - l > 2) withEllipsis.push("...");
      }
      withEllipsis.push(i);
      l = i;
    });
    return withEllipsis;
  }, [currentPage, totalPages]);

  const start = totalRows === 0 ? 0 : Math.min((currentPage - 1) * perPage + 1, totalRows);
  const end   = Math.min(currentPage * perPage, totalRows);

  return (
    <div className="bc-pagination">
      <div className="bc-pagination__info">
        <span>
          Showing <b>{start}</b>–<b>{end}</b> of <b>{totalRows}</b>
        </span>
        <select
          className="bc-pagination__perpage"
          value={perPage}
          onChange={(e) => onPerPageChange(Number(e.target.value))}
        >
          {[10, 25, 50, 100].map((n) => (<option key={n} value={n}>{n} / page</option>))}
        </select>
      </div>
      <div className="bc-pagination__pages">
        <button
          className="bc-pagination__btn"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Previous page"
        >
          <ChevronLeftIcon size={16} />
        </button>
        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`ellipsis-${i}`} className="bc-pagination__ellipsis">…</span>
          ) : (
            <button
              key={p}
              className={`bc-pagination__btn ${p === currentPage ? "is-active" : ""}`}
              onClick={() => onPageChange(p)}
              aria-current={p === currentPage ? "page" : undefined}
            >
              {p}
            </button>
          )
        )}
        <button
          className="bc-pagination__btn"
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Next page"
        >
          <ChevronRightIcon size={16} />
        </button>
      </div>
    </div>
  );
};

/* ────────── Toast ────────── */
const SuccessToast = ({ message, onClose }) => (
  <div className="dept-toast dept-toast--success" role="alert">
    <span className="dept-toast__icon"><CheckCircleIcon size={20} /></span>
    <span className="dept-toast__msg">{message}</span>
    <button className="dept-toast__close" onClick={onClose} type="button" aria-label="Close"><CloseIcon size={15} /></button>
  </div>
);
const ErrorToast = ({ message, onClose }) => (
  <div className="dept-toast dept-toast--error" role="alert">
    <span className="dept-toast__icon"><XCircleIcon size={20} /></span>
    <span className="dept-toast__msg">{message}</span>
    <button className="dept-toast__close" onClick={onClose} type="button" aria-label="Close"><CloseIcon size={15} /></button>
  </div>
);

/* ────────── Custom Confirm Dialog ────────── */
const ConfirmDialog = ({ isOpen, title, message, onConfirm, onCancel, confirmLabel = "Confirm", confirmClass = "danger" }) => {
  if (!isOpen) return null;
  return (
    <div className="bc-confirm-overlay" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="bc-confirm-box">
        <div className={`bc-confirm-icon ${confirmClass}`}>
          <AlertTriangleIcon size={24} />
        </div>
        <h3 className="bc-confirm-title">{title}</h3>
        <p className="bc-confirm-msg">{message}</p>
        <div className="bc-confirm-actions">
          <button type="button" className="bc-btn-cancel" onClick={onCancel}>Cancel</button>
          <button type="button" className={`bc-confirm-btn ${confirmClass}`} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

/* ────────── Main Component ────────── */
const DoctorList = () => {
  const [loading, setLoading]                         = useState(false);
  const [doctors, setDoctors]                         = useState([]);
  const [filterText, setFilterText]                   = useState("");
  const [statusFilter, setStatusFilter]               = useState([]);
  const [approvedFilter, setApprovedFilter]           = useState([]);

  const [messagealert, setMessageAlert]               = useState("");
  const [messageType, setMessageType]                 = useState("");

  const [togglingStatusId, setTogglingStatusId]       = useState(null);
  const [togglingApprovedId, setTogglingApprovedId]   = useState(null);

  // Server pagination state (no filter active)
  const [page, setPage]           = useState(1);
  const [perPage, setPerPage]     = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  // All-records cache used when a filter is active
  const [allDoctors, setAllDoctors]   = useState([]);
  const [allLoaded, setAllLoaded]     = useState(false);

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false, title: "", message: "", onConfirm: null, confirmLabel: "Confirm", confirmClass: "danger",
  });

  const token = localStorage.getItem("authToken");

  const isFiltering = filterText.trim() !== "" || statusFilter.length > 0 || approvedFilter.length > 0;

  /* ── Fetch one server page (normal mode) ── */
  const fetchDoctors = useCallback((pg = 1, size = perPage) => {
    setLoading(true);
    fetch(`${API_BASE_URL}api/doctors?page=${pg}&per_page=${size}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          setDoctors(data.data);
          setTotalRows(data.pagination.total);
          setPage(data.pagination.current_page);
        }
      })
      .finally(() => setLoading(false));
  }, [token, perPage]);

  /* ── Fetch ALL records once when filter is first applied ── */
  const fetchAllDoctors = useCallback(() => {
    if (allLoaded) return;
    setLoading(true);
    fetch(`${API_BASE_URL}api/doctors?page=1&per_page=99999`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          setAllDoctors(data.data);
          setAllLoaded(true);
        }
      })
      .finally(() => setLoading(false));
  }, [token, allLoaded]);

  useEffect(() => { fetchDoctors(1, perPage); }, []);

  /* ── When a filter is turned on, load all records ── */
  useEffect(() => {
    if (isFiltering) fetchAllDoctors();
  }, [isFiltering]);

  /* ── When filter changes, reset to page 1 ── */
  useEffect(() => {
    setPage(1);
  }, [filterText, statusFilter, approvedFilter]);

  const flashMessage = (msg, type = "success") => {
    setMessageAlert(msg);
    setMessageType(type);
    setTimeout(() => { setMessageAlert(""); setMessageType(""); }, 4500);
  };

  const showConfirm = ({ title, message, onConfirm, confirmLabel = "Confirm", confirmClass = "danger" }) => {
    setConfirmDialog({ isOpen: true, title, message, onConfirm, confirmLabel, confirmClass });
  };
  const closeConfirm = () => setConfirmDialog((prev) => ({ ...prev, isOpen: false }));

  /* ── Status Toggle ── */
  const handleStatusChange = (id, currentStatus) => {
    const newStatus = currentStatus === "Active" ? "Inactive" : "Active";
    showConfirm({
      title: "Update Status",
      message: `Are you sure you want to change status to "${newStatus}"?`,
      confirmLabel: "Yes, Update",
      confirmClass: "primary",
      onConfirm: () => {
        closeConfirm();
        setTogglingStatusId(id);
        fetch(`${API_BASE_URL}api/doctors/status/${id}`, {
          method: "GET",
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              flashMessage(`Status updated to ${newStatus}`, "success");
              // Invalidate all-records cache so it refreshes next filter
              setAllLoaded(false);
              setAllDoctors([]);
              fetchDoctors(page, perPage);
            } else {
              flashMessage("Failed to update status.", "danger");
            }
          })
          .catch(() => flashMessage("Error updating status.", "danger"))
          .finally(() => setTogglingStatusId(null));
      },
    });
  };

  /* ── Approved Toggle ── */
  const handleApprovedChange = (id, currentStatus) => {
    const newStatus = currentStatus === "Yes" ? "No" : "Yes";
    showConfirm({
      title: "Update Approval",
      message: `Are you sure you want to change approval to "${newStatus}"?`,
      confirmLabel: "Yes, Update",
      confirmClass: "primary",
      onConfirm: () => {
        closeConfirm();
        setTogglingApprovedId(id);
        fetch(`${API_BASE_URL}api/doctors/approved/${id}`, {
          method: "GET",
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              flashMessage(`Approval updated to ${newStatus}`, "success");
              setAllLoaded(false);
              setAllDoctors([]);
              fetchDoctors(page, perPage);
            } else {
              flashMessage("Failed to update approval.", "danger");
            }
          })
          .catch(() => flashMessage("Error updating approval.", "danger"))
          .finally(() => setTogglingApprovedId(null));
      },
    });
  };

  /* ── Delete ── */
  const handleDelete = (id) => {
    showConfirm({
      title: "Delete Doctor",
      message: "Are you sure you want to delete this doctor? This action cannot be undone.",
      confirmLabel: "Yes, Delete",
      confirmClass: "danger",
      onConfirm: () => {
        closeConfirm();
        fetch(`${API_BASE_URL}api/doctors/destroy/${id}`, {
          method: "GET",
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              flashMessage("Doctor deleted successfully!", "success");
              setAllLoaded(false);
              setAllDoctors([]);
              fetchDoctors(page, perPage);
            } else {
              flashMessage("Failed to delete doctor.", "danger");
            }
          })
          .catch(() => flashMessage("Error deleting doctor.", "danger"));
      },
    });
  };

  /* ────────── Derived display data ──────────
   *
   * Two modes:
   *   A) No filter  → show exactly what the server returned for current page.
   *                   Pagination uses server totalRows.
   *   B) Filter on  → apply filters to allDoctors (all records), then paginate
   *                   the result locally here.
   */
  const filteredAll = useMemo(() => {
    if (!isFiltering) return [];
    let result = allDoctors;
    if (filterText.trim()) {
      const q = filterText.toLowerCase();
      result = result.filter((d) => d.name?.toLowerCase().includes(q));
    }
    if (statusFilter.length > 0) {
      result = result.filter((d) => statusFilter.includes(d.status));
    }
    if (approvedFilter.length > 0) {
      result = result.filter((d) => approvedFilter.includes(d.is_approved));
    }
    return result;
  }, [isFiltering, allDoctors, filterText, statusFilter, approvedFilter]);

  // What we actually show in the table
  const tableData = useMemo(() => {
    if (!isFiltering) return doctors; // server already paged this
    const start = (page - 1) * perPage;
    return filteredAll.slice(start, start + perPage);
  }, [isFiltering, doctors, filteredAll, page, perPage]);

  // Totals & pages for the pagination widget
  const displayTotal = isFiltering ? filteredAll.length : totalRows;
  const totalPages   = Math.max(1, Math.ceil(displayTotal / perPage));

  /* ── Pagination handlers ── */
  const handlePageChange = (pg) => {
    setPage(pg);
    if (!isFiltering) fetchDoctors(pg, perPage);
    // filtering: tableData slice recomputes automatically
  };
  const handlePerPageChange = (n) => {
    setPerPage(n);
    setPage(1);
    if (!isFiltering) fetchDoctors(1, n);
  };

  /* ── Columns ── */
  const columns = useMemo(
    () => [
      {
        name: "Image",
        cell: (row) => {
          let image = null;
          try { const imgs = JSON.parse(row.images); image = imgs[0]; }
          catch { image = row.images; }
          return (
            <div className="dept-img-wrap" style={{ width: 52, height: 52 }}>
              <img
                src={image ? `${API_BASE_URL}public/doctorimages/${image}` : `${API_BASE_URL}public/noimage.jpg`}
                alt={row.name}
                className="dept-img"
              />
            </div>
          );
        },
        width: "80px",
        ignoreRowClick: true,
      },
      {
        name: "Doctor Name",
        selector: (row) => row.name,
        cell: (row) => <span className="bc-cell-name">{row.name}</span>,
        sortable: true,
        grow: 2,
        wrap: true,
      },
      {
        name: "Department",
        selector: (row) => row.department || "—",
        cell: (row) => <span className="bc-slug-pill">{row.department || "—"}</span>,
        sortable: true,
        width: "130px",
      },
      {
        name: "Gender",
        selector: (row) => row.gender || "—",
        cell: (row) => <span className="bc-cell-muted">{row.gender || "—"}</span>,
        sortable: true,
        width: "100px",
      },
      {
        name: "Experience",
        selector: (row) => row.experience || "—",
        cell: (row) => <span className="bc-cell-muted">{row.experience || "—"}</span>,
        sortable: true,
        width: "110px",
      },
      {
        name: "In-Patient ₹",
        selector: (row) => row.consult_price ? parseFloat(row.consult_price).toFixed(2) : "0.00",
        cell: (row) => (
          <span className="bc-price-pill">
            ₹{row.consult_price ? parseFloat(row.consult_price).toFixed(2) : "0.00"}
          </span>
        ),
        sortable: true,
        right: true,
        width: "110px",
      },
      {
        name: "Video ₹",
        selector: (row) => row.videocall_price ? parseFloat(row.videocall_price).toFixed(2) : "0.00",
        cell: (row) => (
          <span className="bc-price-pill bc-price-pill--video">
            ₹{row.videocall_price ? parseFloat(row.videocall_price).toFixed(2) : "0.00"}
          </span>
        ),
        sortable: true,
        right: true,
        width: "110px",
      },
      {
        name: "Status",
        cell: (row) => (
          <ToggleSwitch
            checked={row.status === "Active"}
            disabled={togglingStatusId === row.id}
            onChange={() => handleStatusChange(row.id, row.status)}
          />
        ),
        width: "90px",
        ignoreRowClick: true,
      },
      {
        name: "Approved",
        cell: (row) => (
          <ToggleSwitch
            checked={row.is_approved === "Yes"}
            disabled={togglingApprovedId === row.id}
            onChange={() => handleApprovedChange(row.id, row.is_approved)}
          />
        ),
        width: "100px",
        ignoreRowClick: true,
      },
      {
        name: "Created",
        selector: (row) => row.created_at,
        cell: (row) => (
          <span className="bc-cell-muted">
            {row.created_at
              ? new Date(row.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
              : "—"}
          </span>
        ),
        sortable: true,
        width: "130px",
      },
      {
        name: "Actions",
        cell: (row) => (
          <div className="bc-row-actions">
            <Link to={`/doctor/edit/${row.id}`} className="bc-ico-btn edit" title="Edit Doctor">
              <EditIcon size={15} />
            </Link>
            <Link to={`/doctor/availablity/${row.id}`} className="bc-ico-btn avail" title="Availability">
              <CalendarIcon size={15} />
            </Link>
            <button className="bc-ico-btn del" onClick={() => handleDelete(row.id)} title="Delete Doctor">
              <TrashIcon size={15} />
            </button>
          </div>
        ),
        width: "130px",
        right: true,
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [togglingStatusId, togglingApprovedId]
  );

  /* ── Table Styles ── */
  const tableStyles = {
    table: { style: { background: "transparent" } },
    headRow: {
      style: {
        background: "#f0fbf6",
        borderTopLeftRadius: "12px",
        borderTopRightRadius: "12px",
        borderBottom: "1px solid #d6efe2",
        minHeight: "52px",
      },
    },
    headCells: {
      style: {
        fontSize: "11.5px",
        fontWeight: 700,
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        color: "#5b6b66",
        paddingLeft: "16px",
        paddingRight: "16px",
      },
    },
    rows: {
      style: {
        minHeight: "72px",
        fontSize: "14px",
        color: "#1f2a37",
        borderBottom: "1px solid #eef4f1 !important",
        transition: "background 0.18s ease",
      },
      highlightOnHoverStyle: {
        background: "#f6fcf9",
        borderBottomColor: "#e3f0e9",
        outline: "none",
      },
      stripedStyle: { background: "#fafefb" },
    },
    cells: { style: { paddingLeft: "16px", paddingRight: "16px" } },
    pagination: { style: { display: "none" } },
  };

  return (
    <>
      <style>{`
        :root {
          --bc-bg:           #f3faf6;
          --bc-card:         #ffffff;
          --bc-border:       #e3f0e9;
          --bc-text:         #0f1f1a;
          --bc-muted:        #6b7a74;
          --bc-primary:      #10b981;
          --bc-primary-d:    #059669;
          --bc-primary-grad: linear-gradient(135deg, #14b8a6 0%, #10b981 100%);
          --bc-soft:         #ecfdf5;
          --bc-edit:         #fff1e6;
          --bc-edit-fg:      #ea7a3b;
          --bc-avail:        #eff6ff;
          --bc-avail-fg:     #3b82f6;
          --bc-del:          #fee4e2;
          --bc-del-fg:       #e0413c;
          --bc-shadow:       0 1px 2px rgba(16,30,24,.04), 0 4px 14px rgba(16,30,24,.04);
        }

        .bc-shell { background: var(--bc-bg); min-height: 100%; padding: 24px; }

        /* ── Page header ── */
        .bc-head {
          display: flex; align-items: flex-start;
          justify-content: space-between; gap: 16px;
          flex-wrap: wrap; margin-bottom: 20px;
        }
        .bc-head h2 {
          font-size: clamp(22px, 2.4vw, 30px); font-weight: 800;
          color: var(--bc-text); margin: 0 0 4px 0; letter-spacing: -0.01em;
        }
        .bc-head p { color: var(--bc-muted); font-size: 14px; margin: 0; }
        .bc-add-btn {
          display: inline-flex; align-items: center; gap: 8px;
          background: var(--gradient-brand); color: #fff;
          border: none; padding: 12px 20px; font-weight: 600; font-size: 14px;
          border-radius: 12px; cursor: pointer; text-decoration: none;
          box-shadow: 0 6px 18px rgba(16,185,129,.25);
          transition: transform .15s ease, box-shadow .2s ease, filter .2s ease;
          white-space: nowrap;
        }
        .bc-add-btn svg { fill: none !important; }
        .bc-add-btn:hover { transform: translateY(-1px); filter: brightness(1.05); box-shadow: 0 10px 24px rgba(16,185,129,.32); color: #fff; text-decoration: none; }

        /* ── Card ── */
        .bc-card {
          background: var(--bc-card); border: 1px solid var(--color-border);
          border-radius: 18px; box-shadow: var(--bc-shadow); overflow: hidden;
        }
        .bc-card-top {
          display: flex; align-items: center; justify-content: space-between;
          gap: 12px; padding: 18px 18px 14px; flex-wrap: wrap;
          border-bottom: 1px solid #f0f6f3;
        }
        .bc-card-top-left { display: flex; align-items: center; gap: 10px; flex: 1 1 320px; flex-wrap: wrap; }

        /* ── Search ── */
        .bc-search-box { position: relative; flex: 1 1 240px; max-width: 380px; }
        .bc-search-box svg { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #9aa8a2; pointer-events: none; fill: none !important; }
        .bc-search-box input {
          width: 100%; padding: 11px 14px 11px 42px;
          border-radius: 999px; border: 1px solid var(--color-border);
          background: #f6fbf8; font-size: 14px; color: var(--bc-text); outline: none;
          transition: border-color .15s, box-shadow .15s, background .15s;
        }
        .bc-search-box input::placeholder { color: #9aa8a2; }
        .bc-search-box input:focus { border-color: var(--bc-primary-grad); background: #fff; box-shadow: 0 0 0 4px rgba(16,185,129,.12); }

        /* ── Total Badge ── */
        .bc-total-badge {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 9px 14px; background: #fff; border: 1px solid var(--color-border);
          border-radius: 999px; font-size: 13px; color: var(--bc-text); font-weight: 600; white-space: nowrap;
        }
        .bc-total-badge svg { fill: none !important; color: var(--bc-primary-grad); }
        .bc-total-badge b { color: var(--bc-primary-d); }

        /* ── Multi-select ── */
        .bc-multiselect { flex: 1 1 160px; max-width: 220px; z-index: 20; }
        .bc-multiselect__trigger {
          display: flex; align-items: center; justify-content: space-between;
          gap: 8px; padding: 9px 12px 9px 14px;
          background: #f6fbf8; border: 1px solid var(--color-border);
          border-radius: 10px; cursor: pointer; font-size: 13.5px;
          color: var(--bc-text); user-select: none;
          transition: border-color .15s, box-shadow .15s, background .15s; min-height: 42px;
        }
        .bc-multiselect__trigger:hover, .bc-multiselect__trigger:focus {
          border-color: var(--bc-primary-grad); background: #fff; box-shadow: 0 0 0 4px rgba(16,185,129,.12); outline: none;
        }
        .bc-multiselect__label { display: flex; gap: 5px; align-items: center; flex-wrap: wrap; flex: 1; color: #9aa8a2; }
        .bc-multiselect__label:has(.bc-multiselect__tag) { color: var(--bc-text); }
        .bc-multiselect__tag {
          display: inline-flex; align-items: center;
          background: var(--bc-soft); color: #047857;
          border: 1px solid #b7e8d2; border-radius: 999px;
          padding: 2px 9px; font-size: 12px; font-weight: 600;
        }
        .bc-multiselect__caret { display: inline-flex; align-items: center; gap: 4px; color: #9aa8a2; }
        .bc-multiselect__clear { display: inline-flex; align-items: center; justify-content: center; background: none; border: none; cursor: pointer; color: #9aa8a2; padding: 2px; transition: color .15s; }
        .bc-multiselect__clear:hover { color: var(--bc-del-fg); }
        .bc-multiselect__clear svg { fill: none !important; }
        .bc-multiselect__dropdown {
          position: absolute; top: calc(100% + 6px); left: 0; right: 0;
          background: #fff; border: 1px solid var(--color-border);
          border-radius: 12px; box-shadow: 0 12px 32px rgba(16,30,24,.12);
          overflow: hidden; animation: bcDropIn .18s ease; z-index: 100;
        }
        @keyframes bcDropIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        .bc-multiselect__option {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 14px; cursor: pointer; font-size: 13.5px; color: var(--bc-text); transition: background .12s;
        }
        .bc-multiselect__option:hover { background: #f6fbf8; }
        .bc-multiselect__option.is-selected { background: var(--bc-soft); }
        .bc-multiselect__check {
          width: 18px; height: 18px; border-radius: 5px;
          border: 1.5px solid #c7d3ce;
          display: inline-flex; align-items: center; justify-content: center;
          flex-shrink: 0; background: #fff; transition: all .12s;
        }
        .bc-multiselect__option.is-selected .bc-multiselect__check { background: var(--bc-primary-grad); border-color: var(--bc-primary-grad); }
        .bc-multiselect__check svg { fill: none !important; color: #fff; }

        /* ── Filter row ── */
        .bc-filter-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .bc-filter-label { font-size: 12px; font-weight: 600; color: var(--bc-muted); white-space: nowrap; }

        /* ── iPhone Toggle ── */
        .dept-toggle {
          display: inline-flex; align-items: center;
          width: 46px; height: 26px;
          border-radius: 999px; border: none; cursor: pointer;
          padding: 3px; transition: background .3s cubic-bezier(.4,0,.2,1);
          position: relative; flex-shrink: 0;
          box-shadow: inset 0 1px 3px rgba(0,0,0,.15);
        }
        .dept-toggle:focus-visible { outline: 3px solid rgba(16,185,129,.4); outline-offset: 2px; }
        .dept-toggle:disabled { opacity: .5; cursor: not-allowed; }
        .dept-toggle.is-on  { background: var(--bc-primary-grad); }
        .dept-toggle.is-off { background: #d1d5db; }
        .dept-toggle__thumb {
          width: 20px; height: 20px; border-radius: 50%;
          background: #fff; transition: transform .3s cubic-bezier(.4,0,.2,1);
          box-shadow: 0 2px 6px rgba(0,0,0,.22);
        }
        .dept-toggle.is-on  .dept-toggle__thumb { transform: translateX(20px); }
        .dept-toggle.is-off .dept-toggle__thumb { transform: translateX(0); }

        /* ── Doctor image ── */
        .dept-img-wrap {
          border-radius: 10px; overflow: hidden;
          border: 1px solid var(--color-border);
          box-shadow: 0 2px 6px rgba(16,30,24,.06); flex-shrink: 0;
        }
        .dept-img { width: 100%; height: 100%; object-fit: cover; display: block; }

        /* ── Cell styles ── */
        .bc-cell-name { font-weight: 600; color: var(--bc-text); }
        .bc-cell-muted { color: var(--bc-muted); font-size: 13px; }
        .bc-slug-pill {
          display: inline-block; padding: 4px 12px;
          background: #f1f6f4; color: #4b5b56;
          border-radius: 999px; font-size: 12.5px; font-weight: 500;
          font-family: "SFMono-Regular","Consolas","Liberation Mono",monospace;
        }
        .bc-price-pill {
          display: inline-flex; align-items: center;
          background: #f0fdf4; color: #15803d;
          border: 1px solid #bbf7d0; border-radius: 8px;
          padding: 4px 10px; font-size: 13px; font-weight: 600;
        }
        .bc-price-pill--video { background: #eff6ff; color: #1d4ed8; border-color: #bfdbfe; }

        /* ── Row actions ── */
        .bc-row-actions { display: inline-flex; gap: 5px; }
        .bc-ico-btn {
          width: 34px; height: 34px;
          display: inline-flex; align-items: center; justify-content: center;
          border-radius: 9px; border: none; cursor: pointer; text-decoration: none;
          transition: transform .15s, filter .15s;
        }
        .bc-ico-btn svg { fill: none !important; }
        .bc-ico-btn:hover { transform: translateY(-1px); filter: brightness(.95); }
        .bc-ico-btn.edit  { background: var(--bc-edit);  color: var(--bc-edit-fg); }
        .bc-ico-btn.avail { background: var(--bc-avail); color: var(--bc-avail-fg); }
        .bc-ico-btn.del   { background: var(--bc-del);   color: var(--bc-del-fg); }

        /* ── Custom checkboxes ── */
        .rdt_TableHead input[type="checkbox"],
        .rdt_TableBody input[type="checkbox"] {
          appearance: none; -webkit-appearance: none;
          width: 18px; height: 18px;
          border: 1.5px solid #c7d3ce; border-radius: 5px;
          background: #fff; cursor: pointer; position: relative;
          transition: all .15s ease; margin: 0;
        }
        .rdt_TableHead input[type="checkbox"]:checked,
        .rdt_TableBody input[type="checkbox"]:checked { background: var(--bc-primary-grad); border-color: var(--bc-primary-grad); }
        .rdt_TableHead input[type="checkbox"]:checked::after,
        .rdt_TableBody input[type="checkbox"]:checked::after {
          content: ""; position: absolute;
          left: 4px; top: 1px; width: 6px; height: 10px;
          border: solid #fff; border-width: 0 2px 2px 0; transform: rotate(45deg);
        }

        /* ── Pagination ── */
        .bc-pagination {
          display: flex; align-items: center; justify-content: space-between;
          gap: 12px; flex-wrap: wrap;
          padding: 14px 18px; border-top: 1px solid #eef4f1;
          font-size: 13px; color: var(--bc-muted);
        }
        .bc-pagination__info { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .bc-pagination__info b { color: var(--bc-text); }
        .bc-pagination__perpage {
          padding: 6px 10px; border-radius: 8px; border: 1px solid var(--color-border);
          background: #f6fbf8; font-size: 13px; color: var(--bc-text); cursor: pointer; outline: none;
        }
        .bc-pagination__perpage:focus { border-color: var(--bc-primary-grad); }
        .bc-pagination__pages { display: flex; align-items: center; gap: 4px; }
        .bc-pagination__btn {
          display: inline-flex; align-items: center; justify-content: center;
          min-width: 34px; height: 34px; padding: 0 6px;
          border-radius: 8px; border: 1px solid var(--color-border);
          background: #fff; font-size: 13.5px; font-weight: 500;
          color: var(--bc-text); cursor: pointer; transition: all .15s ease;
        }
        .bc-pagination__btn svg { fill: none !important; }
        .bc-pagination__btn:hover:not(:disabled) { border-color: var(--bc-primary-grad); color: var(--bc-primary-grad); background: var(--bc-soft); }
        .bc-pagination__btn.is-active {
          background: var(--gradient-brand); color: #fff;
          border-color: transparent; box-shadow: 0 4px 12px rgba(16,185,129,.25); font-weight: 700;
        }
        .bc-pagination__btn:disabled { opacity: .4; cursor: not-allowed; }
        .bc-pagination__ellipsis { display: inline-flex; align-items: center; justify-content: center; width: 30px; color: var(--bc-muted); }

        /* ── Loading ── */
        .bc-overlay {
          position: fixed; inset: 0; z-index: 1080;
          background: rgba(255,255,255,.55); backdrop-filter: blur(2px);
          display: grid; place-items: center;
        }
        .bc-spinner {
          width: 42px; height: 42px; border-radius: 50%;
          border: 3px solid #d6efe2; border-top-color: var(--bc-primary-grad);
          animation: bcSpin 0.8s linear infinite;
        }
        @keyframes bcSpin { to { transform: rotate(360deg); } }

        /* ── Empty ── */
        .bc-empty { padding: 48px 20px; text-align: center; color: var(--bc-muted); }
        .bc-empty svg { fill: none !important; color: #c7d3ce; margin-bottom: 12px; }
        .bc-empty p { font-size: 15px; font-weight: 500; margin: 0; }
        .bc-empty span { font-size: 13px; }

        /* ── Toast ── */
        .dept-toast {
          display: flex; align-items: center; gap: 12px;
          padding: 14px 18px; border-radius: 14px;
          margin: 0 0 18px 0; font-size: 14px; font-weight: 500;
          animation: bcDropIn .3s cubic-bezier(.34,1.56,.64,1);
          box-shadow: 0 4px 20px rgba(0,0,0,.09);
        }
        .dept-toast--success { background: #ecfdf5; color: #065f46; border: 1px solid #b7e8d2; }
        .dept-toast--error   { background: #fff1f0; color: #991b1b; border: 1px solid #fbd7d3; }
        .dept-toast__icon svg { fill: none !important; }
        .dept-toast__msg { flex: 1; }
        .dept-toast__close {
          display: inline-flex; align-items: center; justify-content: center;
          background: transparent; border: none; cursor: pointer;
          color: inherit; opacity: .65; padding: 4px; border-radius: 6px; transition: opacity .15s;
        }
        .dept-toast__close:hover { opacity: 1; }
        .dept-toast__close svg { fill: none !important; }

        /* ── Confirm Dialog ── */
        .bc-confirm-overlay {
          position: fixed; inset: 0; z-index: 1060;
          background: rgba(10,20,15,.45); backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center;
          padding: 16px; animation: bcFadeIn .2s ease;
        }
        @keyframes bcFadeIn { from { opacity: 0; } to { opacity: 1; } }
        .bc-confirm-box {
          background: #fff; border-radius: 20px;
          box-shadow: 0 30px 60px rgba(16,30,24,.2), 0 0 0 1px rgba(16,185,129,.08);
          width: 100%; max-width: 400px; padding: 32px 28px 24px; text-align: center;
          animation: bcSlideUp .25s cubic-bezier(0.34,1.56,0.64,1);
        }
        @keyframes bcSlideUp { from { opacity: 0; transform: translateY(28px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .bc-confirm-icon {
          width: 56px; height: 56px; border-radius: 16px;
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 16px; flex-shrink: 0;
        }
        .bc-confirm-icon.danger  { background: var(--bc-del);  color: var(--bc-del-fg); }
        .bc-confirm-icon.primary { background: var(--bc-soft); color: var(--bc-primary-grad); }
        .bc-confirm-icon svg { fill: none !important; }
        .bc-confirm-title { font-size: 17px; font-weight: 700; color: var(--bc-text); margin: 0 0 8px; letter-spacing: -.01em; }
        .bc-confirm-msg   { font-size: 14px; color: var(--bc-muted); margin: 0 0 24px; line-height: 1.6; }
        .bc-confirm-actions { display: flex; gap: 10px; justify-content: center; }
        .bc-btn-cancel {
          padding: 10px 18px; border-radius: 10px;
          background: #fff; border: 1px solid var(--color-border);
          font-size: 14px; font-weight: 500; color: var(--bc-muted); cursor: pointer; transition: all .15s;
        }
        .bc-btn-cancel:hover { background: #f6fbf8; color: var(--bc-text); }
        .bc-confirm-btn {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 10px 22px; border-radius: 10px;
          font-size: 14px; font-weight: 600; cursor: pointer;
          border: none; transition: filter .15s, box-shadow .15s;
        }
        .bc-confirm-btn.danger  { background: var(--bc-del-fg); color: #fff; box-shadow: 0 4px 12px rgba(224,65,60,.25); }
        .bc-confirm-btn.danger:hover  { filter: brightness(1.08); }
        .bc-confirm-btn.primary { background: var(--gradient-brand); color: #fff; box-shadow: 0 4px 12px rgba(16,185,129,.25); }
        .bc-confirm-btn.primary:hover { filter: brightness(1.05); }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .bc-shell { padding: 14px; }
          .bc-head { flex-direction: column; align-items: stretch; }
          .bc-add-btn { width: 100%; justify-content: center; }
          .bc-card-top { flex-direction: column; align-items: stretch; }
          .bc-search-box { max-width: 100%; }
          .bc-multiselect { max-width: 100%; }
          .bc-pagination { flex-direction: column; align-items: center; gap: 10px; }
        }

        .bc-shell svg, .bc-confirm-overlay svg { fill: none; }
      `}</style>

      <div id="layout-wrapper">
        <Header />

        {loading && (
          <div className="bc-overlay">
            <div className="bc-spinner" />
          </div>
        )}

        <div className="main-content">
          <div className="page-content">
            <div className="bc-shell">

              {/* ── Page Header ── */}
              <div className="bc-head">
                <div>
                  <h2>Doctors</h2>
                  <p>Manage and organise all your doctors in one place.</p>
                </div>
                <Link to="/doctor/add" className="bc-add-btn">
                  <PlusIcon size={18} />
                  Add Doctor
                </Link>
              </div>

              {/* ── Toasts ── */}
              {messagealert && messageType === "success" && (
                <SuccessToast message={messagealert} onClose={() => setMessageAlert("")} />
              )}
              {messagealert && messageType === "danger" && (
                <ErrorToast message={messagealert} onClose={() => setMessageAlert("")} />
              )}

              {/* ── Main Card ── */}
              <div className="bc-card">
                {/* Toolbar */}
                <div className="bc-card-top">
                  <div className="bc-card-top-left">
                    <div className="bc-search-box">
                      <SearchIcon size={17} />
                      <input
                        type="text"
                        placeholder="Search by doctor name…"
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                      />
                    </div>
                    <div className="bc-filter-row">
                      <span className="bc-filter-label">Status:</span>
                      <MultiSelect
                        options={["Active", "Inactive"]}
                        value={statusFilter}
                        onChange={setStatusFilter}
                        placeholder="All statuses"
                      />
                    </div>
                    <div className="bc-filter-row">
                      <span className="bc-filter-label">Approved:</span>
                      <MultiSelect
                        options={["Yes", "No"]}
                        value={approvedFilter}
                        onChange={setApprovedFilter}
                        placeholder="All"
                      />
                    </div>
                  </div>
                  <div className="bc-total-badge">
                    <UserMdIcon size={16} />
                    Total:&nbsp;<b>{displayTotal}</b>
                  </div>
                </div>

                {/* Data Table */}
                <DataTable
                  columns={columns}
                  data={tableData}
                  customStyles={tableStyles}
                  highlightOnHover
                  responsive
                  striped
                  pagination={false}
                  noDataComponent={
                    <div className="bc-empty">
                      <UserMdIcon size={40} />
                      <p>No doctors found</p>
                      <span>Try adjusting your search filters</span>
                    </div>
                  }
                />

                {/* Custom Pagination */}
                {displayTotal > 0 && (
                  <CustomPagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    perPage={perPage}
                    totalRows={displayTotal}
                    onPerPageChange={handlePerPageChange}
                  />
                )}
              </div>
            </div>
          </div>

          {/* ── Confirm Dialog ── */}
          <ConfirmDialog
            isOpen={confirmDialog.isOpen}
            title={confirmDialog.title}
            message={confirmDialog.message}
            onConfirm={confirmDialog.onConfirm}
            onCancel={closeConfirm}
            confirmLabel={confirmDialog.confirmLabel}
            confirmClass={confirmDialog.confirmClass}
          />

          <Footer />
        </div>
      </div>
    </>
  );
};

export default DoctorList;